"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { HireLocationDetail } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
  formatHireLocationLabel,
} from "~/lib/marketplace/hire-location-utils";
import {
  type HireTeamRosterEntry,
  rosterEntryLabel,
} from "~/lib/marketplace/hire-team-roster";

type HireAddLocationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (location: HireLocationDetail) => void;
  onEdit?: (index: number, location: HireLocationDetail) => void;
  editIndex?: number | null;
  initialLocation?: HireLocationDetail | null;
  teamRoster?: HireTeamRosterEntry[];
  defaultLeadEmail?: string;
};

function emptyForm(defaultLeadEmail?: string): HireLocationDetail {
  return {
    name: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    phone: "",
    businessHours: DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
    leadTeamMemberEmail: defaultLeadEmail,
    otherTeamMemberEmails: [],
  };
}

export function HireAddLocationDialog({
  open,
  onOpenChange,
  onAdd,
  onEdit,
  editIndex = null,
  initialLocation = null,
  teamRoster = [],
  defaultLeadEmail,
}: HireAddLocationDialogProps) {
  const isEditing = editIndex != null && initialLocation != null;
  const [values, setValues] = React.useState<HireLocationDetail>(() =>
    initialLocation ?? emptyForm(defaultLeadEmail),
  );

  React.useEffect(() => {
    if (!open) return;
    setValues(initialLocation ?? emptyForm(defaultLeadEmail));
  }, [open, initialLocation, defaultLeadEmail]);

  const leadOptions = teamRoster;
  const otherOptions = teamRoster.filter(
    (entry) => entry.email !== values.leadTeamMemberEmail,
  );

  const toggleOtherMember = (email: string, checked: boolean) => {
    setValues((prev) => {
      const current = prev.otherTeamMemberEmails ?? [];
      const next = checked
        ? [...current, email]
        : current.filter((item) => item !== email);
      return { ...prev, otherTeamMemberEmails: next };
    });
  };

  const canSubmit =
    values.name.trim() &&
    values.address?.trim() &&
    values.city?.trim() &&
    values.state?.trim() &&
    values.postalCode?.trim() &&
    values.phone?.trim() &&
    values.businessHours?.trim() &&
    values.leadTeamMemberEmail?.trim();

  const handleSubmit = () => {
    const detail: HireLocationDetail = {
      name: values.name.trim(),
      address: values.address?.trim(),
      city: values.city?.trim(),
      state: values.state?.trim(),
      postalCode: values.postalCode?.trim(),
      phone: values.phone?.trim(),
      businessHours: values.businessHours?.trim(),
      leadTeamMemberEmail: values.leadTeamMemberEmail?.trim(),
      otherTeamMemberEmails: (values.otherTeamMemberEmails ?? []).filter(Boolean),
    };

    if (isEditing && onEdit && editIndex != null) {
      onEdit(editIndex, detail);
    } else {
      onAdd(detail);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Location" : "Add New Location"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hire-loc-name">Location Name *</Label>
            <Input
              id="hire-loc-name"
              placeholder="Enter location name"
              value={values.name}
              onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-loc-address">Street Address *</Label>
            <Input
              id="hire-loc-address"
              placeholder="Enter street address"
              value={values.address ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, address: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="hire-loc-city">City *</Label>
              <Input
                id="hire-loc-city"
                placeholder="Enter city"
                value={values.city ?? ""}
                onChange={(e) => setValues((prev) => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hire-loc-state">State *</Label>
              <Input
                id="hire-loc-state"
                placeholder="Enter state"
                value={values.state ?? ""}
                onChange={(e) => setValues((prev) => ({ ...prev, state: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-loc-zip">ZIP Code *</Label>
            <Input
              id="hire-loc-zip"
              placeholder="Enter ZIP code"
              value={values.postalCode ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, postalCode: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-loc-phone">Location Phone Number *</Label>
            <Input
              id="hire-loc-phone"
              placeholder="Enter location phone number"
              value={values.phone ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-loc-hours">Business Hours *</Label>
            <Input
              id="hire-loc-hours"
              placeholder="e.g., Mon-Fri 8 AM - 5 PM"
              value={values.businessHours ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, businessHours: e.target.value }))}
            />
          </div>

          {teamRoster.length > 0 ? (
            <>
              <div className="space-y-2">
                <Label>Lead Team Member *</Label>
                <Select
                  value={values.leadTeamMemberEmail ?? ""}
                  onValueChange={(email) =>
                    setValues((prev) => ({
                      ...prev,
                      leadTeamMemberEmail: email,
                      otherTeamMemberEmails: (prev.otherTeamMemberEmails ?? []).filter(
                        (item) => item !== email,
                      ),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select lead team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadOptions.map((entry) => (
                      <SelectItem key={entry.email} value={entry.email}>
                        {rosterEntryLabel(entry)}
                        {entry.role === "employer" ? " · Employer" : entry.role === "lead" ? " · Lead" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {otherOptions.length > 0 ? (
                <div className="space-y-2">
                  <Label>Other Team Members</Label>
                  <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                    {otherOptions.map((entry) => {
                      const checked = (values.otherTeamMemberEmails ?? []).includes(entry.email);
                      return (
                        <label
                          key={entry.email}
                          className="flex cursor-pointer items-start gap-2 text-sm text-slate-700"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(next) =>
                              toggleOtherMember(entry.email, next === true)
                            }
                          />
                          <span>{rosterEntryLabel(entry)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
            {isEditing ? "Save Location" : "Add Location"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type HireLocationDetailCardProps = {
  detail: HireLocationDetail;
  teamRoster?: HireTeamRosterEntry[];
  onEdit?: () => void;
};

export function HireLocationDetailCard({
  detail,
  teamRoster = [],
  onEdit,
}: HireLocationDetailCardProps) {
  const addressLine = [
    detail.address,
    [detail.city, detail.state, detail.postalCode].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  const leadMember = teamRoster.find(
    (entry) => entry.email === detail.leadTeamMemberEmail,
  );
  const otherMembers = (detail.otherTeamMemberEmails ?? [])
    .map((email) => teamRoster.find((entry) => entry.email === email))
    .filter((entry): entry is HireTeamRosterEntry => Boolean(entry));

  return (
    <div className="rounded-md border border-slate-100 bg-white px-3 py-2 text-sm text-slate-700">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-slate-900">{formatHireLocationLabel(detail)}</p>
        {onEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Edit
          </Button>
        ) : null}
      </div>
      {addressLine ? <p className="mt-1 text-xs text-slate-600">{addressLine}</p> : null}
      {detail.phone ? <p className="mt-0.5 text-xs text-slate-600">Phone: {detail.phone}</p> : null}
      {detail.businessHours ? (
        <p className="mt-0.5 text-xs text-slate-600">Hours: {detail.businessHours}</p>
      ) : null}
      {leadMember ? (
        <p className="mt-2 text-xs text-slate-600">
          <span className="font-medium text-slate-700">Lead Team Member:</span> {leadMember.name}
        </p>
      ) : null}
      {otherMembers.length > 0 ? (
        <p className="mt-0.5 text-xs text-slate-600">
          <span className="font-medium text-slate-700">Other Team Members:</span>{" "}
          {otherMembers.map((member) => member.name).join(", ")}
        </p>
      ) : null}
    </div>
  );
}
